import {
  AdditionalProperties,
  EmptyAdditionalProperties,
  ValidAdditionalProperties,
} from 'ApplicationEntry/models/AdditionalProperties';
import {AgentInformation, EmptyAgent, ValidAgent} from 'ApplicationEntry/models/AgentInformation';
import {AgentList, EmptyAgentList, ValidAgentList} from 'ApplicationEntry/models/AgentList';
import {
  EmptyGroupAWDStatus,
  GroupAWDStatus,
  ValidGroupAWDStatus,
} from 'ApplicationEntry/models/AWDStatus/GroupAWDStatus';
import {BillingModes, EmptyBillingModes, ValidBillingModes} from 'ApplicationEntry/models/BillingModes';
import {
  ContingentBeneficiary,
  EmptyContingentBeneficiary,
  ValidContingentBeneficiary,
} from 'ApplicationEntry/models/ContingentBeneficiary';
import {
  ContingentBeneficiaryList,
  EmptyContingentBeneficiaryList,
  ValidContingentBeneficiaryList,
} from 'ApplicationEntry/models/ContingentBeneficiaryList';
import {DependentLists, EmptyDependentList, ValidDependentList} from 'ApplicationEntry/models/DependentLists';
import {Dependents, EmptyDependents, ValidDependents} from 'ApplicationEntry/models/Dependents';
import {
  EmptyGroupApplicantDemographics,
  GroupApplicantDemographics,
  ValidGroupApplicantDemographics,
} from 'ApplicationEntry/models/GroupApplicantDemographics';
import {
  EmptyGroupBillingAgent,
  GroupBillingAgent,
  ValidGroupBillingAgent,
} from 'ApplicationEntry/models/GroupBillingAgent';
import {EmptyGroupCaseDetails, GroupCaseDetails, ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {
  EmptyGroupVerifyCriticalInformation,
  GroupVerifyCriticalInformation,
  ValidGroupVerifyCriticalInformation,
} from 'ApplicationEntry/models/GroupVerifyCriticalInformation';
import {EmptyHippaTrustees, HippaTrustees, ValidHippaTrustees} from 'ApplicationEntry/models/HippaTrustees';
import {EmptyMessage, Messages, ValidMessage} from 'ApplicationEntry/models/Messages';
import {EmptyOwnerInfo, OwnerInfo, ValidOwnerInfo} from 'ApplicationEntry/models/OwnerInfo';
import {EmptyPlanCode, PlanCode, ValidPlanCode} from 'ApplicationEntry/models/PlanCode';
import {EmptyPlanCodes, PlanCodes, ValidPlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {EmptyPolicyRecord, PolicyRecord, ValidPolicyRecord} from 'ApplicationEntry/models/PolicyRecord';
import {EmptyPremium, Premium, ValidPremium} from 'ApplicationEntry/models/Premium';
import {
  EmptyPrimaryBeneficiary,
  PrimaryBeneficiary,
  ValidPrimaryBeneficiary,
} from 'ApplicationEntry/models/PrimaryBeneficiary';
import {
  EmptyPrimaryBeneficiaryList,
  PrimaryBeneficiaryList,
  ValidPrimaryBeneficiaryList,
} from 'ApplicationEntry/models/PrimaryBeneficiaryList';
import {
  EmptyGroupAccident,
  GroupAccident,
  ValidGroupAccident,
} from 'ApplicationEntry/models/ProductsRiders/Accident/GroupAccident';
import {
  EmptyGroupAccidentNewYork,
  GroupAccidentNewYork,
  ValidGroupAccidentNewYork,
} from 'ApplicationEntry/models/ProductsRiders/AccidentNewYork/GroupAccidentNewYork';
import {EmptyGroupAIP, GroupAIP, ValidGroupAIP} from 'ApplicationEntry/models/ProductsRiders/AIP/GroupAIP';
import {
  EmptyGroupCancer,
  GroupCancer,
  ValidGroupCancer,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/GroupCancer';
import {
  EmptyGroupCriticalIllness,
  GroupCriticalIllness,
  ValidGroupCriticalIllness,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/GroupCriticalIllness';
import {
  EmptyGroupCriticalIllnessNY,
  GroupCriticalIllnessNY,
  ValidGroupCriticalIllnessNY,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllnessNY/GroupCriticalIllnessNY';
import {
  EmptyGroupDisability,
  GroupDisability,
  ValidGroupDisability,
} from 'ApplicationEntry/models/ProductsRiders/Disability/GroupDisability';
import {
  EmptyGroupGLTERM,
  GroupGLTERM,
  ValidGroupGLTERM,
} from 'ApplicationEntry/models/ProductsRiders/GLTERM/GroupGLTERM';
import {
  EmptyGroupHeartStroke,
  GroupHeartStroke,
  ValidGroupHeartStroke,
} from 'ApplicationEntry/models/ProductsRiders/HeartStroke/GroupHeartStroke';
import {EmptyGroupICRTN, GroupICRTN, ValidGroupICRTN} from 'ApplicationEntry/models/ProductsRiders/ICRTN/GroupICRTN';
import {EmptyGroupIWL, GroupIWL, ValidGroupIWL} from 'ApplicationEntry/models/ProductsRiders/IWL/GroupIWL';
import {EmptyGroupShop, GroupShop, ValidGroupShop} from 'ApplicationEntry/models/ProductsRiders/Shop/GroupShop';
import {EmptyGroupUL, GroupUL, ValidGroupUL} from 'ApplicationEntry/models/ProductsRiders/UniversalLife/GroupUL';
import {
  EmptySecondaryAddressee,
  SecondaryAddressee,
  ValidSecondaryAddressee,
} from 'ApplicationEntry/models/SecondaryAddressee';
import {EmptyUWQuestions, UWQuestions, ValidUWQuestions} from 'ApplicationEntry/models/UnderwritingQuestions';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {EmptyGroupPinCare, GroupPinCare, ValidGroupPinCare} from './ProductsRiders/PinCare/GroupPinCare';

type GroupProps = {
  caseDetails: GroupCaseDetails;
  applicantDemographics: GroupApplicantDemographics;
  ownerInfo: OwnerInfo;
  secondaryAddressee: SecondaryAddressee;
  primaryBeneficiaryList: PrimaryBeneficiaryList;
  primaryBeneficiary: PrimaryBeneficiary;
  contingentBeneficiary: ContingentBeneficiary;
  contingentBeneficiaryList: ContingentBeneficiaryList;
  criticalIllness: GroupCriticalIllness;
  icrtn: GroupICRTN;
  cancer: GroupCancer;
  dependents: Dependents;
  dependentLists: DependentLists;
  policyRecord: PolicyRecord;
  billingAgent: GroupBillingAgent;
  agentInformation: AgentInformation;
  agentList: AgentList;
  messages: Messages;
  billingModes: BillingModes;
  awdStatus: GroupAWDStatus;
  verifyCriticalInformation: GroupVerifyCriticalInformation;
  disability: GroupDisability;
  shop: GroupShop;
  iwl: GroupIWL;
  heartStroke: GroupHeartStroke;
  accident: GroupAccident;
  uwQuestions: UWQuestions;
  universalLife: GroupUL;
  glterm: GroupGLTERM;
  pinCare: GroupPinCare;
  accidentNewYork: GroupAccidentNewYork;
  criticalIllnessNY: GroupCriticalIllnessNY;
  hippaTrustees: HippaTrustees;
  premium: Premium;
  aip: GroupAIP;
  planCode: PlanCode;
  planCodes: PlanCodes;
  additionalProperties: AdditionalProperties;
};

export class InvalidGroupApplication {
  public readonly key = 'InvalidGroupApplication';

  constructor(
    public readonly caseDetails: GroupCaseDetails,
    public readonly applicantDemographics: GroupApplicantDemographics,
    public readonly ownerInfo: OwnerInfo,
    public readonly secondaryAddressee: SecondaryAddressee,
    public readonly primaryBeneficiaryList: PrimaryBeneficiaryList,
    public readonly primaryBeneficiary: PrimaryBeneficiary,
    public readonly contingentBeneficiary: ContingentBeneficiary,
    public readonly contingentBeneficiaryList: ContingentBeneficiaryList,
    public readonly criticalIllness: GroupCriticalIllness,
    public readonly icrtn: GroupICRTN,
    public readonly cancer: GroupCancer,
    public readonly dependents: Dependents,
    public readonly dependentLists: DependentLists,
    public readonly policyRecord: PolicyRecord,
    public readonly billingAgent: GroupBillingAgent,
    public readonly agentInformation: AgentInformation,
    public readonly agentList: AgentList,
    public readonly messages: Messages,
    public readonly billingModes: BillingModes,
    public readonly awdStatus: GroupAWDStatus,
    public readonly verifyCriticalInformation: GroupVerifyCriticalInformation,
    public readonly disability: GroupDisability,
    public readonly shop: GroupShop,
    public readonly iwl: GroupIWL,
    public readonly heartStroke: GroupHeartStroke,
    public readonly accident: GroupAccident,
    public readonly uwQuestions: UWQuestions,
    public readonly universalLife: GroupUL,
    public readonly glterm: GroupGLTERM,
    public readonly pinCare: GroupPinCare,
    public readonly accidentNewYork: GroupAccidentNewYork,
    public readonly criticalIllnessNY: GroupCriticalIllnessNY,
    public readonly hippaTrustees: HippaTrustees,
    public readonly premium: Premium,
    public readonly aip: GroupAIP,
    public readonly planCode: PlanCode,
    public readonly planCodes: PlanCodes,
    public readonly additionalProperties: AdditionalProperties
  ) {}
}

export class ValidGroupApplication {
  public static createApp({
    caseDetails,
    applicantDemographics,
    ownerInfo,
    secondaryAddressee,
    primaryBeneficiaryList,
    primaryBeneficiary,
    contingentBeneficiary,
    contingentBeneficiaryList,
    criticalIllness,
    icrtn,
    cancer,
    dependents,
    dependentLists,
    policyRecord,
    billingAgent,
    agentInformation,
    agentList,
    messages,
    billingModes,
    awdStatus,
    verifyCriticalInformation,
    disability,
    shop,
    iwl,
    heartStroke,
    accident,
    uwQuestions,
    universalLife,
    glterm,
    pinCare,
    accidentNewYork,
    criticalIllnessNY,
    hippaTrustees,
    premium,
    aip,
    planCode,
    planCodes,
    additionalProperties,
  }: GroupProps) {
    return this.create(
      caseDetails,
      applicantDemographics,
      ownerInfo,
      secondaryAddressee,
      primaryBeneficiaryList,
      primaryBeneficiary,
      contingentBeneficiary,
      contingentBeneficiaryList,
      criticalIllness,
      icrtn,
      cancer,
      dependents,
      dependentLists,
      policyRecord,
      billingAgent,
      agentInformation,
      agentList,
      messages,
      billingModes,
      awdStatus,
      verifyCriticalInformation,
      disability,
      shop,
      iwl,
      heartStroke,
      accident,
      uwQuestions,
      universalLife,
      glterm,
      pinCare,
      accidentNewYork,
      criticalIllnessNY,
      hippaTrustees,
      premium,
      aip,
      planCode,
      planCodes,
      additionalProperties
    );
  }

  public static create(
    caseDetails: GroupCaseDetails,
    applicantDemographics: GroupApplicantDemographics,
    ownerInfo: OwnerInfo,
    secondaryAddressee: SecondaryAddressee,
    primaryBeneficiaryList: PrimaryBeneficiaryList,
    primaryBeneficiary: PrimaryBeneficiary,
    contingentBeneficiary: ContingentBeneficiary,
    contingentBeneficiaryList: ContingentBeneficiaryList,
    criticalIllness: GroupCriticalIllness,
    icrtn: GroupICRTN,
    cancer: GroupCancer,
    dependents: Dependents,
    dependentLists: DependentLists,
    policyRecord: PolicyRecord,
    billingAgent: GroupBillingAgent,
    agentInformation: AgentInformation,
    agentList: AgentList,
    messages: Messages,
    billingModes: BillingModes,
    awdStatus: GroupAWDStatus,
    verifyCriticalInformation: GroupVerifyCriticalInformation,
    disability: GroupDisability,
    shop: GroupShop,
    iwl: GroupIWL,
    heartStroke: GroupHeartStroke,
    accident: GroupAccident,
    uwQuestions: UWQuestions,
    universalLife: GroupUL,
    glterm: GroupGLTERM,
    pinCare: GroupPinCare,
    accidentNewYork: GroupAccidentNewYork,
    criticalIllnessNY: GroupCriticalIllnessNY,
    hippaTrustees: HippaTrustees,
    premium: Premium,
    aip: GroupAIP,
    planCode: PlanCode,
    planCodes: PlanCodes,
    additionalProperties: AdditionalProperties
  ) {
    return caseDetails instanceof ValidGroupCaseDetails &&
      applicantDemographics instanceof ValidGroupApplicantDemographics &&
      ownerInfo instanceof ValidOwnerInfo &&
      secondaryAddressee instanceof ValidSecondaryAddressee &&
      primaryBeneficiaryList instanceof ValidPrimaryBeneficiaryList &&
      primaryBeneficiary instanceof ValidPrimaryBeneficiary &&
      contingentBeneficiary instanceof ValidContingentBeneficiary &&
      contingentBeneficiaryList instanceof ValidContingentBeneficiaryList &&
      criticalIllness instanceof ValidGroupCriticalIllness &&
      icrtn instanceof ValidGroupICRTN &&
      cancer instanceof ValidGroupCancer &&
      dependents instanceof ValidDependents &&
      dependentLists instanceof ValidDependentList &&
      policyRecord instanceof ValidPolicyRecord &&
      billingAgent instanceof ValidGroupBillingAgent &&
      agentInformation instanceof ValidAgent &&
      agentList instanceof ValidAgentList &&
      messages instanceof ValidMessage &&
      billingModes instanceof ValidBillingModes &&
      awdStatus instanceof ValidGroupAWDStatus &&
      verifyCriticalInformation instanceof ValidGroupVerifyCriticalInformation &&
      disability instanceof ValidGroupDisability &&
      shop instanceof ValidGroupShop &&
      iwl instanceof ValidGroupIWL &&
      heartStroke instanceof ValidGroupHeartStroke &&
      accident instanceof ValidGroupAccident &&
      uwQuestions instanceof ValidUWQuestions &&
      universalLife instanceof ValidGroupUL &&
      glterm instanceof ValidGroupGLTERM &&
      accident instanceof ValidGroupAccident &&
      pinCare instanceof ValidGroupPinCare &&
      accidentNewYork instanceof ValidGroupAccidentNewYork &&
      criticalIllnessNY instanceof ValidGroupCriticalIllnessNY &&
      hippaTrustees instanceof ValidHippaTrustees &&
      premium instanceof ValidPremium &&
      aip instanceof ValidGroupAIP &&
      planCode instanceof ValidPlanCode &&
      planCodes instanceof ValidPlanCodes &&
      additionalProperties instanceof ValidAdditionalProperties
      ? new ValidGroupApplication(
          caseDetails,
          applicantDemographics,
          ownerInfo,
          secondaryAddressee,
          primaryBeneficiaryList,
          primaryBeneficiary,
          contingentBeneficiary,
          contingentBeneficiaryList,
          criticalIllness,
          icrtn,
          cancer,
          dependents,
          dependentLists,
          policyRecord,
          billingAgent,
          agentInformation,
          agentList,
          messages,
          billingModes,
          awdStatus,
          verifyCriticalInformation,
          disability,
          shop,
          iwl,
          heartStroke,
          accident,
          uwQuestions,
          universalLife,
          glterm,
          pinCare,
          accidentNewYork,
          criticalIllnessNY,
          hippaTrustees,
          premium,
          aip,
          planCode,
          planCodes,
          additionalProperties
        )
      : new InvalidGroupApplication(
          caseDetails,
          applicantDemographics,
          ownerInfo,
          secondaryAddressee,
          primaryBeneficiaryList,
          primaryBeneficiary,
          contingentBeneficiary,
          contingentBeneficiaryList,
          criticalIllness,
          icrtn,
          cancer,
          dependents,
          dependentLists,
          policyRecord,
          billingAgent,
          agentInformation,
          agentList,
          messages,
          billingModes,
          awdStatus,
          verifyCriticalInformation,
          disability,
          shop,
          iwl,
          heartStroke,
          accident,
          uwQuestions,
          universalLife,
          glterm,
          pinCare,
          accidentNewYork,
          criticalIllnessNY,
          hippaTrustees,
          premium,
          aip,
          planCode,
          planCodes,
          additionalProperties
        );
  }

  public readonly key = 'ValidGroupApplication';

  private constructor(
    public readonly caseDetails: ValidGroupCaseDetails,
    public readonly applicantDemographics: ValidGroupApplicantDemographics,
    public readonly ownerInfo: ValidOwnerInfo,
    public readonly secondaryAddressee: SecondaryAddressee,
    public readonly primaryBeneficiaryList: ValidPrimaryBeneficiaryList,
    public readonly primaryBeneficiary: ValidPrimaryBeneficiary,
    public readonly contingentBeneficiary: ValidContingentBeneficiary,
    public readonly contingentBeneficiaryList: ValidContingentBeneficiaryList,
    public readonly criticalIllness: ValidGroupCriticalIllness,
    public readonly icrtn: ValidGroupICRTN,
    public readonly cancer: ValidGroupCancer,
    public readonly dependents: Dependents,
    public readonly dependentLists: DependentLists,
    public readonly policyRecord: PolicyRecord,
    public readonly billingAgent: ValidGroupBillingAgent,
    public readonly agentInformation: ValidAgent,
    public readonly agentList: ValidAgentList,
    public readonly messages: ValidMessage,
    public readonly billingModes: BillingModes,
    public readonly awdStatus: GroupAWDStatus,
    public readonly verifyCriticalInformation: ValidGroupVerifyCriticalInformation,
    public readonly disability: ValidGroupDisability,
    public readonly shop: ValidGroupShop,
    public readonly iwl: ValidGroupIWL,
    public readonly heartStroke: ValidGroupHeartStroke,
    public readonly accident: ValidGroupAccident,
    public readonly uwQuestions: UWQuestions,
    public readonly universalLife: ValidGroupUL,
    public readonly glterm: ValidGroupGLTERM,
    public readonly pinCare: ValidGroupPinCare,
    public readonly accidentNewYork: ValidGroupAccidentNewYork,
    public readonly criticalIllnessNY: ValidGroupCriticalIllnessNY,
    public readonly hippaTrustees: HippaTrustees,
    public readonly premium: Premium,
    public readonly aip: GroupAIP,
    public readonly planCode: PlanCode,
    public readonly planCodes: PlanCodes,
    public readonly additionalProperties: AdditionalProperties
  ) {}
}

export type GroupApplication = ValidGroupApplication | InvalidGroupApplication;

export type GroupApplicationOptions<TReturn> = ToMatchOptions<GroupApplication, 'key', TReturn>;

export const matchGroupApplication = <TReturn>(
  groupApplication: GroupApplication,
  options: GroupApplicationOptions<TReturn>
): TReturn => {
  switch (groupApplication.key) {
    case 'InvalidGroupApplication':
      return options.InvalidGroupApplication(groupApplication);
    case 'ValidGroupApplication':
      return options.ValidGroupApplication(groupApplication);
  }
};

export const changeStandardCaseDetails = (caseDetails: GroupCaseDetails) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, caseDetails});

export const changeApplicantDemographics = (applicantDemographics: GroupApplicantDemographics) => (
  app: GroupApplication
) => ValidGroupApplication.createApp({...app, applicantDemographics});

export const changeSecondaryAddressee = (secondaryAddressee: SecondaryAddressee) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, secondaryAddressee});

export const changePrimaryBeneficiaryList = (primaryBeneficiaryList: PrimaryBeneficiaryList) => (
  app: GroupApplication
) => ValidGroupApplication.createApp({...app, primaryBeneficiaryList});

export const changePrimaryBeneficiary = (primaryBeneficiary: PrimaryBeneficiary) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, primaryBeneficiary});

export const changeContingentBeneficiary = (contingentBeneficiary: ContingentBeneficiary) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, contingentBeneficiary});

export const changeContingentBeneficiaryList = (contingentBeneficiaryList: ContingentBeneficiaryList) => (
  app: GroupApplication
) => ValidGroupApplication.createApp({...app, contingentBeneficiaryList});

export const changeDependents = (dependents: Dependents) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, dependents});

export const changeDependentLists = (dependentLists: DependentLists) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, dependentLists});

export const changePolicyRecord = (policyRecord: PolicyRecord) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, policyRecord});

export const changeBillingAgent = (billingAgent: GroupBillingAgent) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, billingAgent});

export const changeAgent = (agentInformation: AgentInformation) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, agentInformation});

export const changeAgentList = (agentList: AgentList) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, agentList});

export const changeMessages = (messages: Messages) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, messages});

export const changeBillingModes = (billingModes: BillingModes) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, billingModes});

export const changeAWDStatus = (awdStatus: GroupAWDStatus) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, awdStatus});

export const changeVerifyCriticalInformation = (verifyCriticalInformation: GroupVerifyCriticalInformation) => (
  app: GroupApplication
) => ValidGroupApplication.createApp({...app, verifyCriticalInformation});

export const changeDisability = (disability: GroupDisability) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, disability});

export const changeShop = (shop: GroupShop) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, shop});

export const changeIWL = (iwl: GroupIWL) => (app: GroupApplication) => ValidGroupApplication.createApp({...app, iwl});

export const changeCancer = (cancer: GroupCancer) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, cancer});

export const changeCriticalIllness = (criticalIllness: GroupCriticalIllness) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, criticalIllness});

export const changeICRTN = (icrtn: GroupICRTN) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, icrtn});

export const changeHeartStroke = (heartStroke: GroupHeartStroke) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, heartStroke});

export const changeAccident = (accident: GroupAccident) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, accident});

export const changeUWQuestions = (uwQuestions: UWQuestions) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, uwQuestions});

export const changeUniversalLife = (universalLife: GroupUL) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, universalLife});

export const changeGLTERM = (glterm: GroupGLTERM) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, glterm});

export const changePinCare = (pinCare: GroupPinCare) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, pinCare});

export const changeAccidentNewYork = (accidentNewYork: GroupAccidentNewYork) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, accidentNewYork});

export const changeCriticalIllnessNY = (criticalIllnessNY: GroupCriticalIllnessNY) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, criticalIllnessNY});

export const changeHippaTrustees = (hippaTrustees: HippaTrustees) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, hippaTrustees});

export const changePremium = (premium: Premium) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, premium});

export const changeAIP = (aip: GroupAIP) => (app: GroupApplication) => ValidGroupApplication.createApp({...app, aip});

export const changePlanCode = (planCode: PlanCode) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, planCode});

export const changePlanCodes = (planCodes: PlanCodes) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, planCodes});

export const changeAdditionalProperties = (additionalProperties: AdditionalProperties) => (app: GroupApplication) =>
  ValidGroupApplication.createApp({...app, additionalProperties});

export const EmptyGroupApplication = new InvalidGroupApplication(
  EmptyGroupCaseDetails,
  EmptyGroupApplicantDemographics,
  EmptyOwnerInfo,
  EmptySecondaryAddressee,
  EmptyPrimaryBeneficiaryList,
  EmptyPrimaryBeneficiary,
  EmptyContingentBeneficiary,
  EmptyContingentBeneficiaryList,
  EmptyGroupCriticalIllness,
  EmptyGroupICRTN,
  EmptyGroupCancer,
  EmptyDependents,
  EmptyDependentList,
  EmptyPolicyRecord,
  EmptyGroupBillingAgent,
  EmptyAgent,
  EmptyAgentList,
  EmptyMessage,
  EmptyBillingModes,
  EmptyGroupAWDStatus,
  EmptyGroupVerifyCriticalInformation,
  EmptyGroupDisability,
  EmptyGroupShop,
  EmptyGroupIWL,
  EmptyGroupHeartStroke,
  EmptyGroupAccident,
  EmptyUWQuestions,
  EmptyGroupUL,
  EmptyGroupGLTERM,
  EmptyGroupPinCare,
  EmptyGroupAccidentNewYork,
  EmptyGroupCriticalIllnessNY,
  EmptyHippaTrustees,
  EmptyPremium,
  EmptyGroupAIP,
  EmptyPlanCode,
  EmptyPlanCodes,
  EmptyAdditionalProperties
);
